import { io } from "socket.io-client";
import { getCookie } from "../config/cookie";

// Realtime client for the self-hosted Socket.IO gateway (arkchat-backend-pubnub).
// Keeps the interface the app was built around (addListener, removeListener,
// subscribe, unsubscribeAll, publish) and authenticates with the session cookie;
// the server joins rooms from the user's stored channel permissions.
//
// - The server joins the socket to every channel the user may read; this
//   client only delivers events for channels the app has subscribed to, so
//   handlers see exactly what they saw with PubNub.
// - Message events: { channel, subscription, timetoken, message, publisher }.
// - publish() sends `message:send` and resolves { timetoken } on ack, or
//   rejects with error.status.category set to the server's reason ("offline"
//   when not connected). A big group message goes without `to` (frameFor).

const SOCKET_PATH = "/v2/pubnub/socket.io";
const SEND_TIMEOUT_MS = 10000;
// The app calls unsubscribeAll() right before subscribe() whenever its channel
// list changes; only disconnect if no subscribe follows.
const UNSUBSCRIBE_GRACE_MS = 2000;
// A group message to many people (everyone in a big group, or a private one
// to many of them) makes a big frame with its `to` list, an entry per person.
// Above this size it's sent without `to`; the gateway fills `to` in for the
// message's participants (from the group's members) before delivering and
// saving it.
const COMPACT_ABOVE_BYTES = 48 * 1024;

function frameFor(channel, message) {
  const data = message?.content?.message;
  const compactable =
    message?.content?.type !== "direct-message" &&
    Array.isArray(data?.to) && data.to.length > 0 &&
    Array.isArray(data.participants) && data.participants.length > 0;
  if (!compactable) return { channel, message };
  if (new TextEncoder().encode(JSON.stringify({ channel, message })).length <= COMPACT_ABOVE_BYTES) {
    return { channel, message };
  }
  // Only when the participants cover everyone in `to`: that's who it's filled in for.
  const participants = new Set(data.participants.map(String));
  if (!data.to.every((u) => participants.has(String(u?.id)))) return { channel, message };
  return {
    channel,
    message: { ...message, content: { ...message.content, message: { ...data, to: [], expand_recipients: true } } },
  };
}

export default class RealtimeClient {
  constructor({ baseUrl }) {
    this.origin = new URL(baseUrl || window.location.origin, window.location.origin).origin;
    this.listeners = new Set();
    this.channels = new Set();
    this.socket = null;
    this.connectedOnce = false;
    this.disconnectTimer = null;
    this.retryTimer = null;
  }

  ensureSocket() {
    if (this.socket) {
      // Only once it has stopped trying (rejected or disconnected): calling
      // connect() while it's still connecting sends a second connect request,
      // and the server closes the connection for it ("forced close").
      if (!this.socket.active && !this.retryTimer) this.socket.connect();
      return this.socket;
    }
    const token = getCookie("token");
    console.info(`[realtime] connecting to ${this.origin}${SOCKET_PATH} (session token ${token ? "present" : "MISSING"})`);
    const socket = io(this.origin, {
      path: SOCKET_PATH,
      transports: ["websocket"],
      // Read the cookie on every (re)connect so a refreshed session is used.
      // features: messages to everyone come without their recipient lists.
      auth: (cb) => cb({ token: getCookie("token"), features: ["compact-recipients"] }),
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      randomizationFactor: 0.5,
    });

    socket.on("connect", () => {
      console.info(`[realtime] connected (${socket.io.engine.transport.name}), subscribed channels: ${this.channels.size}`);
      const category = this.connectedOnce ? "PNReconnectedCategory" : "PNConnectedCategory";
      this.connectedOnce = true;
      this.emitStatus({ category });
    });
    socket.on("disconnect", (reason) => {
      console.warn(`[realtime] disconnected: ${reason}`);
      this.emitStatus({ category: "PNNetworkDownCategory", reason });
    });
    socket.on("connect_error", (err) => {
      // unauthorized = session rejected; auth_unavailable = server couldn't check it;
      // "websocket error"/"timeout" = network/proxy never let the socket through.
      console.warn(`[realtime] connect_error: ${err?.message}`);
      // Rejections from the server's auth step are not retried by Socket.IO.
      // "auth_unavailable" is temporary; "unauthorized" means the session is
      // gone and the app's own 401 handling logs the user out.
      if (!socket.active && err?.message === "auth_unavailable" && !this.retryTimer) {
        this.retryTimer = setTimeout(() => {
          this.retryTimer = null;
          if (this.channels.size > 0) socket.connect();
        }, 3000 + Math.random() * 7000);
      }
    });
    socket.on("event", (envelope) => {
      if (!envelope || !this.channels.has(envelope.channel)) return;
      const messageEvent = {
        channel: envelope.channel,
        subscription: envelope.channel,
        timetoken: envelope.timetoken,
        message: envelope.message,
        publisher: envelope.publisher,
      };
      this.listeners.forEach((listener) => {
        try {
          if (listener.message) listener.message(messageEvent);
        } catch (e) {
          console.error("[realtime] listener error", e);
        }
      });
    });

    this.socket = socket;
    return socket;
  }

  emitStatus(statusEvent) {
    this.listeners.forEach((listener) => {
      try {
        if (listener.status) listener.status(statusEvent);
      } catch (e) {
        console.error("[realtime] status listener error", e);
      }
    });
  }

  addListener(listener) {
    this.listeners.add(listener);
  }

  removeListener(listener) {
    this.listeners.delete(listener);
  }

  subscribe({ channels = [] } = {}) {
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }
    channels.forEach((c) => this.channels.add(c));
    this.ensureSocket();
  }

  unsubscribeAll() {
    this.channels.clear();
    if (this.disconnectTimer) clearTimeout(this.disconnectTimer);
    this.disconnectTimer = setTimeout(() => {
      this.disconnectTimer = null;
      if (this.channels.size === 0 && this.socket) this.socket.disconnect();
    }, UNSUBSCRIBE_GRACE_MS);
  }

  publish({ channel, message }) {
    const socket = this.ensureSocket();
    if (!socket.connected) {
      // Don't let Socket.IO buffer it: the message stays pending and is
      // resent once on reconnect (realtime/outbox), never twice.
      const error = new Error("offline");
      error.status = { category: "offline" };
      return Promise.reject(error);
    }
    return new Promise((resolve, reject) => {
      socket.timeout(SEND_TIMEOUT_MS).emit("message:send", frameFor(channel, message), (err, ack) => {
        if (err) {
          console.warn(`[realtime] message:send timed out (socket ${socket.connected ? "connected" : "NOT connected"})`);
          reject(new Error("realtime_send_timeout"));
          return;
        }
        if (ack && ack.ok) {
          resolve({ timetoken: ack.timetoken });
          return;
        }
        console.warn(`[realtime] message:send rejected: ${ack && ack.error}`);
        const error = new Error((ack && ack.error) || "realtime_send_failed");
        error.status = { category: (ack && ack.error) || "realtime_send_failed" };
        reject(error);
      });
    });
  }

  destroy() {
    if (this.disconnectTimer) clearTimeout(this.disconnectTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.listeners.clear();
    this.channels.clear();
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }
}
